import React from 'react'
import { fontFamilies } from '@fedsim/constants'
import { secondsToTime, numberFormatCurrency } from '@fedsim/helpers'
import { SegmentsMeta } from '@fedsim/constants'
import { styled } from '@mui/material/styles'
import { Stack, Typography, Box, } from '@mui/material'

import TvIcon from '@mui/icons-material/Tv';
import { green } from '@mui/material/colors'
import AvTimerIcon from '@mui/icons-material/AvTimer'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import CompleteIcon from '@mui/icons-material/CheckBox'
import InCompleteIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CostIcon from '@mui/icons-material/MonetizationOn'
import RatingIcon from '@mui/icons-material/TrendingUp'
import SegmentTypeIcon from '@mui/icons-material/Receipt'

import { Chip } from '../chip'
import SpacedPaper from './spaced-paper'
import SpacedDivider from './spaced-divider'
import DottedPaper from './dotted-paper'
import GameButton from './game-button'

export { GameButton, DottedPaper, SpacedPaper, SpacedDivider }

export const SPACING = 1

export const Directional = {
  row: 'row',
  column: 'column'
} as const;

export type DirectionalEnum = typeof Directional[keyof typeof Directional];

export const Sizes = {
  small: "small",
  medium: "medium",
  large: "large"
} as const;

export type SizesEnum = typeof Sizes[keyof typeof Sizes];


export const CostRow = ({
  Icon = CostIcon,
  size = Sizes.medium,
  value,
  label
}): JSX.Element => {
  const labelStr = `${label}: ${numberFormatCurrency(value)}`

  return <Chip variant="filled" color="success" size={size} avatar={<Icon />} label={labelStr} />
}

export const SegmentType = ({
  children = 0,
  Icon = SegmentTypeIcon
}): JSX.Element => {
  const label = `${SegmentsMeta[children]?.name}`

  return (
    <Stack alignItems="center"
      justifyContent="space-between"
      direction="row">
      <Icon />
      <Caption>
        {label}
      </Caption>
    </Stack>
  )
}

export const SegmentCost = ({
  children = 'DEFAULT',
  Icon = CostIcon
}) => {
  const label = `Cost ${numberFormatCurrency(SegmentsMeta[children]?.cost)}`

  return (
    <Stack alignItems="center"
      justifyContent="space-between"
      direction="row">
      <Icon />
      <Caption>
        {label}
      </Caption>
    </Stack>
  )
}

export const Special = ({
  value = false
}) => (
  <Chip
    variant="filled" color={value ? 'primary' : 'success'}
    avatar={<TvIcon />}
    label={!value ? 'Regular' : 'Special'}
  />
)

export const HeaderPrimary = props => (
  <Typography
    variant='h1'
    {...props}
    sx={{
      fontFamily: fontFamilies.primaryFont,
      fontSize: 20,
      textTransform: 'uppercase',
      ...props?.sx
    }}
  >
    {props?.children}
  </Typography>
)

export const HeaderSecondary = props => (
  <Typography
    variant='h2'
    {...props}
    sx={{
      ...props?.sx,
      fontFamily: fontFamilies.secondaryFont,
      fontWeight: 'bold',
      fontSize: 20,
      textTransform: 'capitalize'
    }}
  >
    {props?.children}
  </Typography>
)

export const NamePlate = ({ noWrap = false, name = '', ...rest }) => {
  return <HeaderSecondary noWrap={noWrap} {...rest}>{name}</HeaderSecondary>
}

export const HeaderTertiary = props => (
  <Typography
    variant='h3'
    {...props}
    sx={{
      ...props?.sx,
      fontFamily: fontFamilies.secondaryFont,
      fontWeight: 'bolder',
      fontSize: 18
    }}
  >
    {props?.children}
  </Typography>
)

export const HeaderBody = props => (
  <Typography
    variant='h6'
    {...props}
    sx={{
      ...props?.sx,
      fontFamily: fontFamilies.bodyFont,
      fontSize: 16
    }}
  >
    {props?.children}
  </Typography>
)

export const Body = ({ children }) => (
  <Typography variant='body1' sx={{ fontSize: 19 }}>
    {children}
  </Typography>
)

export const Caption = ({ children = null, ...props }) => (
  <Typography variant='caption' {...props} sx={{ fontSize: 15, ...props?.sx }}>
    {children}
  </Typography>
)

export const Subtitle = ({ children = null, ...props }) => (
  <Typography variant='subtitle1' {...props}>
    {children}
  </Typography>
)

export const Currency = ({ children = null, color = green[500] }) => (
  <Typography variant='subtitle2' sx={{ color, fontSize: 18 }}>
    {children}
  </Typography>
)

export const Cost = ({
  children = 0,
  Icon = CostIcon
}) => {
  const label = "Cost: "
  const labelStr = `${label}${numberFormatCurrency(children)}`

  return <Chip variant="filled" color="primary" avatar={<Icon color="primary" />} label={labelStr} />
}

export const Duration = ({
  value = 0,
  Icon = AvTimerIcon
}): JSX.Element => {
  const label = `Duration: ${secondsToTime(Number(value))}`

  return (
    <Stack alignItems="center"
      justifyContent="space-between"
      direction="row">
      <Icon />
      <Caption>
        {label}
      </Caption>
    </Stack>
  )
}

export const DurationTimer = ({
  value = 0,
  size = Sizes.small,
  ...rest
}) => {
  const label = secondsToTime(Number(value))

  return <Chip label={label} size={size} {...rest} />
}

export const Completion = ({ value = 0 }) => {
  const Icon = value ? CompleteIcon : InCompleteIcon
  const label = value ? 'Complete' : 'In progress'

  return <Chip variant="filled" color="primary" avatar={<Icon />} label={label} />
}

export const Income = ({
  value = 0,
  Icon = CostIcon
}) => {
  const label = `Income: ${Number(value)}`

  return <Chip variant="filled" color="primary" avatar={<Icon />} label={label} />
}

export const Balance = ({
  value = 0,
  Icon = CostIcon,
  fullWidth = false
}) => {
  const label = numberFormatCurrency(value)

  return (
    <Chip
      variant="filled"
      avatar={<Icon />}
      label={label}
      sx={{
        bgcolor: 'green',
        color: 'white',
        width: 'fit-content',
      }}
    />
  )
}

export const DateDisplay = ({
  children = 0,
  color = "primary",
  prefix = '',
  Icon = CalendarMonthIcon
}) => {
  const label = `${prefix && `${prefix}: `}${new Date(children).toDateString()}`

  return (
    <Stack alignItems="center"
      justifyContent="space-between"
      direction="row">
      <Icon />
      <Caption>
        {label}
      </Caption>
    </Stack>
  )
}

export const ReignLength = ({
  value = new Date(),
  prefix = '',
  Icon = CalendarMonthIcon
}): JSX.Element => {
  const label = `${prefix && `${prefix}: `}${new Date(value).toDateString()}`

  return <Chip variant="filled" color="primary" avatar={<Icon />} label={label} />
}

export const Rating = ({
  children = 0,
  Icon = RatingIcon
}): JSX.Element => {
  const label = `Rating: ${Number(children)}`

  return <Chip variant="filled" color="primary" avatar={<Icon />} label={label} />
}

export const Spacer = styled(props => <Box {...props} />)(({ theme }) => ({
  marginBottom: theme.spacing(SPACING * 3)
}))

export const NamesStack = ({ names = [], join = <>&nbsp; VS &nbsp;</> }): JSX.Element => {
  return (
    <>
      {names.map((name, index) => (
        <React.Fragment key={name}>
          <HeaderBody>{name}</HeaderBody>
          {names.length - 1 !== index ? <Caption>{join}</Caption> : null}
        </React.Fragment>
      ))}
    </>
  )
}
